// @ts-check

import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getSpacesPresignedUrl } from "@minohealth/common/services/SpacesService";

/**
 * @param {Object} options
 * @param {string} [options.url] - The remote URL of the image to load.
 * @param {File} [options.localFile] - A local file object (e.g., from an input).
 * @param {string} [options.userToken]
 */
export function useChatImage({ url, localFile, userToken }) {
  /** @type {ReactState<string>} */
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);

  useEffect(() => {
    if (localFile == null) return;
    const objectUrl = URL.createObjectURL(localFile);
    setLocalPreviewUrl(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
      setLocalPreviewUrl(null);
    };
  }, [localFile]);

  const { data: remotePreviewUrl, isLoading, isError, error } = useQuery({
    queryKey: ["CHAT-IMAGE", url],
    queryFn: () => getSpacesPresignedUrl({ objectUrl: url, userToken }),
    enabled: localFile == null && url != null,
    staleTime: 50 * 60 * 1000, // presigned URLs expire in 1h; refresh before that
    refetchOnWindowFocus: false,
  });

  const isActuallyLoading = (() => {
    if (localFile != null) return localPreviewUrl == null;
    if (url != null) return isLoading;
    return true;
  })();

  return {
    previewUrl: localFile != null ? localPreviewUrl : remotePreviewUrl,
    isLoading: isActuallyLoading,
    isError,
    error,
  };
}
